import { useEffect, useState, useMemo } from "react";
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from "react-leaflet";
import L from "leaflet";
import {
  ISTRES_CENTER, ISTRES_PARKINGS, ISTRES_DELIVERY, ISTRES_COMMERCES,
  ISTRES_KIOSKS, ISTRES_DEFIBRILLATORS, ISTRES_MARKETS,
  ISTRES_ZONES_BLEUES, ISTRES_DEPOSE_MINUTE,
  ISTRES_PLAYGROUNDS, ISTRES_SPORTS, ISTRES_ANIMATIONS, itineraryUrl,
} from "@/lib/istres";

/* Flat SVG icons drawn directly into Leaflet divIcon — clean, modern, no emoji. */
function svgIcon(svgInner, color = "#1E6FB8", size = 36) {
  const html = `
    <div style="
      width:${size}px;height:${size}px;border-radius:14px;
      background:${color};color:#fff;
      display:flex;align-items:center;justify-content:center;
      box-shadow:0 4px 12px rgba(15,46,81,0.32);
      border:2px solid #fff;
    ">
      <svg width="${size * 0.55}" height="${size * 0.55}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
        ${svgInner}
      </svg>
    </div>`;
  return L.divIcon({
    className: "phenix-flat-marker",
    html,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

const PATHS = {
  parking: '<rect x="4" y="3" width="16" height="18" rx="3"/><path d="M9 17V7h4a3 3 0 010 6H9"/>',
  bike: '<circle cx="6" cy="17" r="3"/><circle cx="18" cy="17" r="3"/><path d="M6 17l4-9h4l2 9"/><path d="M10 8l-2-3h2"/>',
  car: '<path d="M5 17h14M5 17l1-5h12l1 5M5 17v2M19 17v2"/><rect x="3" y="11" width="18" height="6" rx="2"/><circle cx="8" cy="17" r="1.5"/><circle cx="16" cy="17" r="1.5"/>',
  package: '<path d="M3 7l9-4 9 4v10l-9 4-9-4V7z"/><path d="M3 7l9 4 9-4M12 11v10"/>',
  locker: '<rect x="4" y="4" width="16" height="16" rx="2"/><line x1="12" y1="4" x2="12" y2="20"/><circle cx="8" cy="12" r="0.8" fill="currentColor"/><circle cx="16" cy="12" r="0.8" fill="currentColor"/>',
  walking: '<circle cx="13" cy="4" r="2"/><path d="M9 22l3-9 3 9M9 13l-2 3M15 13l2 3"/>',
  shop: '<path d="M4 7h16l-1 13H5L4 7z"/><path d="M9 7V5a3 3 0 016 0v2"/>',
  info: '<circle cx="12" cy="12" r="9"/><line x1="12" y1="11" x2="12" y2="17"/><circle cx="12" cy="8" r="0.8" fill="currentColor"/>',
  heart: '<path d="M12 21s-7-4.5-7-10a4 4 0 017-2.5A4 4 0 0119 11c0 5.5-7 10-7 10z"/><polyline points="8 12 11 12 12.5 9.5 14 14.5 15.5 12 18 12"/>',
  market: '<path d="M3 8h18l-1 4H4L3 8z"/><path d="M5 12v8M19 12v8M5 20h14"/><path d="M3 8l2-4h14l2 4"/>',
  clock: '<circle cx="12" cy="12" r="9"/><polyline points="12 7 12 12 15 14"/>',
  disc: '<circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/><circle cx="12" cy="12" r="1.5" fill="currentColor"/>',
  playground: '<path d="M5 21v-7M5 14a2 2 0 014 0M5 14h4"/><circle cx="7" cy="7" r="2"/><path d="M14 21v-9l4-2v9M14 12l8 0M18 5v5"/>',
  sports: '<circle cx="12" cy="12" r="9"/><path d="M12 3v18M3 12h18M5.6 5.6l12.8 12.8M5.6 18.4l12.8-12.8"/>',
  animation: '<path d="M3 7l9-4 9 4-9 4-9-4z"/><path d="M3 7v6c0 3 4 5 9 5s9-2 9-5V7"/><circle cx="12" cy="11" r="1" fill="currentColor"/>',
};

const ICON_BY_TYPE = {
  velo: { path: PATHS.bike, color: "#16A34A" },
  pied: { path: PATHS.walking, color: "#16A34A" },
  drive: { path: PATHS.car, color: "#7236CB" },
  relais: { path: PATHS.package, color: "#7236CB" },
  locker: { path: PATHS.locker, color: "#7236CB" },
};

const LAYERS = [
  { id: "commerces", label: "Commerces", color: "bg-sun-400", count: ISTRES_COMMERCES.length, dot: "#FFD500" },
  { id: "parkings", label: "Parkings", color: "bg-plum-600", count: ISTRES_PARKINGS.length, dot: "#1E6FB8" },
  { id: "zonesBleues", label: "Zones bleues", color: "bg-blue-500", count: ISTRES_ZONES_BLEUES.length, dot: "#3B82F6" },
  { id: "deposeMinute", label: "Dépose-minute", color: "bg-orange-500", count: ISTRES_DEPOSE_MINUTE.length, dot: "#F97316" },
  { id: "delivery", label: "Livraison", color: "bg-mint-500", count: ISTRES_DELIVERY.length, dot: "#16A34A" },
  { id: "kiosks", label: "Bornes info", color: "bg-clay-400", count: ISTRES_KIOSKS.length, dot: "#ED3F8B" },
  { id: "defibs", label: "Défibrillateurs", color: "bg-red-500", count: ISTRES_DEFIBRILLATORS.length, dot: "#DC2626" },
  { id: "markets", label: "Marchés", color: "bg-clay-300", count: ISTRES_MARKETS.length, dot: "#C0703F" },
  { id: "playgrounds", label: "Aires de jeux enfants", color: "bg-mint-400", count: ISTRES_PLAYGROUNDS.length, dot: "#10B981" },
  { id: "sports", label: "Sport & clubs", color: "bg-plum-400", count: ISTRES_SPORTS.length, dot: "#7236CB" },
  { id: "animations", label: "Animations & ateliers", color: "bg-pink-400", count: ISTRES_ANIMATIONS.length, dot: "#EC4899" },
];

export default function IstresMap({ height = 540 }) {
  const [active, setActive] = useState({
    commerces: true, parkings: true, delivery: true,
    kiosks: true, defibs: true, markets: true,
    zonesBleues: true, deposeMinute: true,
    playgrounds: true, sports: true, animations: true,
  });
  const [mountKey, setMountKey] = useState(null);

  useEffect(() => {
    setMountKey(`map-${Math.random().toString(36).slice(2)}`);
  }, []);

  const icons = useMemo(() => ({
    parking: svgIcon(PATHS.parking, "#1E6FB8"),
    velo: svgIcon(PATHS.bike, "#16A34A"),
    pied: svgIcon(PATHS.walking, "#16A34A"),
    drive: svgIcon(PATHS.car, "#7236CB"),
    relais: svgIcon(PATHS.package, "#7236CB"),
    locker: svgIcon(PATHS.locker, "#7236CB"),
    commerce: svgIcon(PATHS.shop, "#C99B00"),
    kiosk: svgIcon(PATHS.info, "#ED3F8B"),
    defib: svgIcon(PATHS.heart, "#DC2626"),
    market: svgIcon(PATHS.market, "#C0703F"),
    deposeMinute: svgIcon(PATHS.clock, "#F97316"),
    zoneBleue: svgIcon(PATHS.disc, "#3B82F6", 30),
    playground: svgIcon(PATHS.playground, "#10B981"),
    sport: svgIcon(PATHS.sports, "#7236CB"),
    animation: svgIcon(PATHS.animation, "#EC4899"),
  }), []);

  const toggle = (id) => setActive((a) => ({ ...a, [id]: !a[id] }));

  return (
    <div className="relative rounded-2xl overflow-hidden border border-cream-200 shadow-[0_8px_28px_-12px_rgba(15,46,81,0.25)]" data-testid="istres-map">
      <div style={{ height, width: "100%" }} className="bg-cream-100">
        {mountKey && (
          <MapContainer
            key={mountKey}
            center={[ISTRES_CENTER.lat, ISTRES_CENTER.lng]}
            zoom={15}
            scrollWheelZoom={false}
            style={{ height: "100%", width: "100%" }}
            attributionControl={false}
          >
            <MapFocusListener />
            <TileLayer
              url="https://{s}.tile.openstreetmap.fr/osmfr/{z}/{x}/{y}.png"
              attribution='© OpenStreetMap France'
            />

            {active.parkings && ISTRES_PARKINGS.map((p) => (
              <Marker key={p.id} position={[p.lat, p.lng]} icon={icons.parking}>
                <Popup>
                  <PopupBody title={p.name} subtitle={`${p.capacity} places · ${p.type}`} extra={`Horaires : ${p.hours}`} lat={p.lat} lng={p.lng} parkingId={p.id} />
                </Popup>
              </Marker>
            ))}

            {active.zonesBleues && ISTRES_ZONES_BLEUES.map((z) => (
              <Circle
                key={z.id}
                center={[z.lat, z.lng]}
                radius={z.radius}
                pathOptions={{ color: "#3B82F6", weight: 2, fillColor: "#3B82F6", fillOpacity: 0.18 }}
              >
                <Popup>
                  <PopupBody
                    title={z.name}
                    subtitle={`Zone bleue · ${z.duration}`}
                    extra={`${z.hours} · ${z.note}`}
                    lat={z.lat}
                    lng={z.lng}
                  />
                </Popup>
              </Circle>
            ))}

            {active.zonesBleues && ISTRES_ZONES_BLEUES.map((z) => (
              <Marker key={`zb-pin-${z.id}`} position={[z.lat, z.lng]} icon={icons.zoneBleue}>
                <Popup>
                  <PopupBody
                    title={z.name}
                    subtitle={`Zone bleue · ${z.duration}`}
                    extra={`${z.hours} · ${z.note}`}
                    lat={z.lat}
                    lng={z.lng}
                  />
                </Popup>
              </Marker>
            ))}

            {active.deposeMinute && ISTRES_DEPOSE_MINUTE.map((dm) => (
              <Marker key={dm.id} position={[dm.lat, dm.lng]} icon={icons.deposeMinute}>
                <Popup>
                  <PopupBody
                    title={dm.name}
                    subtitle={`Dépose-minute · ${dm.max}`}
                    extra={`${dm.hours} · ${dm.note}`}
                    lat={dm.lat}
                    lng={dm.lng}
                  />
                </Popup>
              </Marker>
            ))}

            {active.delivery && ISTRES_DELIVERY.map((d) => (
              <Marker key={d.id} position={[d.lat, d.lng]} icon={icons[d.type] || icons.relais}>
                <Popup>
                  <PopupBody title={d.name} subtitle={d.zone} lat={d.lat} lng={d.lng} />
                </Popup>
              </Marker>
            ))}

            {active.commerces && ISTRES_COMMERCES.map((c) => (
              <Marker key={c.id} position={[c.lat, c.lng]} icon={icons.commerce}>
                <Popup>
                  <PopupBody
                    title={c.name}
                    subtitle={`${c.cat} · ${c.hours}`}
                    extra={c.address}
                    lat={c.lat} lng={c.lng}
                    pmr={c.pmr}
                    delivery={c.delivery}
                    phone={c.phone}
                  />
                </Popup>
              </Marker>
            ))}

            {active.kiosks && ISTRES_KIOSKS.map((k) => (
              <Marker key={k.id} position={[k.lat, k.lng]} icon={icons.kiosk}>
                <Popup>
                  <PopupBody title={k.name} subtitle="Borne d'information" extra={`Horaires : ${k.hours}`} lat={k.lat} lng={k.lng} />
                </Popup>
              </Marker>
            ))}

            {active.defibs && ISTRES_DEFIBRILLATORS.map((d) => (
              <Marker key={d.id} position={[d.lat, d.lng]} icon={icons.defib}>
                <Popup>
                  <PopupBody title={d.name} subtitle="Défibrillateur (DAE)" extra={d.access} lat={d.lat} lng={d.lng} />
                </Popup>
              </Marker>
            ))}

            {active.markets && ISTRES_MARKETS.map((m) => (
              <Marker key={m.id} position={[m.lat, m.lng]} icon={icons.market}>
                <Popup>
                  <PopupBody title={m.name} subtitle={`${m.day} · ${m.hours}`} extra={`${m.where} · ${m.desc}`} lat={m.lat} lng={m.lng} />
                </Popup>
              </Marker>
            ))}

            {active.playgrounds && ISTRES_PLAYGROUNDS.map((p) => (
              <Marker key={p.id} position={[p.lat, p.lng]} icon={icons.playground}>
                <Popup>
                  <PopupBody
                    title={p.name}
                    subtitle={`Aire de jeux · ${p.ages}`}
                    extra={`${p.features} · ${p.access}${p.free ? " · Accès libre" : ""}`}
                    lat={p.lat} lng={p.lng}
                  />
                </Popup>
              </Marker>
            ))}

            {active.sports && ISTRES_SPORTS.map((s) => (
              <Marker key={s.id} position={[s.lat, s.lng]} icon={icons.sport}>
                <Popup>
                  <PopupBody
                    title={s.name}
                    subtitle={`${s.cat} · ${s.ages}`}
                    extra={s.desc}
                    lat={s.lat} lng={s.lng}
                    phone={s.contact}
                  />
                </Popup>
              </Marker>
            ))}

            {active.animations && ISTRES_ANIMATIONS.map((a) => (
              <Marker key={a.id} position={[a.lat, a.lng]} icon={icons.animation}>
                <Popup>
                  <PopupBody
                    title={a.name}
                    subtitle={`${a.cat} · ${a.ages}`}
                    extra={`${a.desc} · ${a.schedule}`}
                    lat={a.lat} lng={a.lng}
                  />
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        )}
      </div>

      {/* Layer toggles */}
      <div className="absolute top-3 right-3 bg-white/95 backdrop-blur-md rounded-2xl p-2 shadow-lg flex flex-col gap-0.5 z-[400] max-w-[180px]">
        <div className="phenix-eyebrow px-2 py-1.5">Filtres</div>
        {LAYERS.map((l) => (
          <button
            key={l.id}
            onClick={() => toggle(l.id)}
            data-testid={`map-layer-${l.id}`}
            className={`flex items-center gap-2 px-2.5 py-1.5 rounded-xl text-[11px] font-semibold transition-all ${
              active[l.id] ? "bg-plum-50 text-plum-700" : "text-plum-700/40 hover:bg-cream-50"
            }`}
          >
            <span
              className="w-2 h-2 rounded-full shrink-0"
              style={{ background: l.dot, opacity: active[l.id] ? 1 : 0.3 }}
            />
            {l.label}
            <span className="ml-auto text-[9px] font-bold tabular-nums">{l.count}</span>
          </button>
        ))}
      </div>

      <div className="absolute top-3 left-3 bg-plum-700 text-white rounded-2xl px-4 py-2 shadow-lg z-[400]">
        <div className="text-[9px] font-bold uppercase tracking-widest text-sun-300">Plan dynamique</div>
        <div className="font-display font-bold text-sm">Centre-ville d'Istres</div>
      </div>
    </div>
  );
}

function PopupBody({ title, subtitle, extra, lat, lng, pmr, delivery, phone, parkingId }) {
  const onItinerary = () => {
    if (parkingId) {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/analytics/parking-click`;
      // fire-and-forget — never block navigation
      try {
        fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ parking_id: parkingId, parking_name: title }),
          keepalive: true,
        });
      } catch (_) { /* noop */ }
    }
  };
  return (
    <div className="text-[13px] min-w-[200px]">
      <strong className="text-plum-900 block mb-1">{title}</strong>
      <div className="text-plum-700/70 text-[11px] mb-1">{subtitle}</div>
      {extra && <div className="text-plum-700/60 text-[11px] mb-2">{extra}</div>}
      {phone && (
        <div className="text-plum-700/70 text-[11px] mb-1">📞 <a href={`tel:${phone.replace(/\s/g, "")}`} className="text-plum-700 hover:underline">{phone}</a></div>
      )}
      <div className="flex flex-wrap gap-1 mb-2">
        {pmr && (
          <span className="phenix-pill bg-mint-100 text-mint-600 text-[9px] py-0.5">♿ PMR</span>
        )}
        {delivery && (
          <a href={delivery} target="_blank" rel="noreferrer" className="phenix-pill bg-plum-100 text-plum-700 text-[9px] py-0.5 hover:bg-plum-200">
            🚲 Livraison
          </a>
        )}
      </div>
      <a
        href={itineraryUrl(lat, lng, title)}
        target="_blank"
        rel="noreferrer"
        onClick={onItinerary}
        className="inline-flex items-center gap-1 text-[11px] font-bold text-plum-700 hover:text-plum-900 bg-plum-50 hover:bg-plum-100 px-2.5 py-1.5 rounded-full transition-colors"
      >
        🧭 Itinéraire
      </a>
    </div>
  );
}

function MapFocusListener() {
  const map = useMap();
  useEffect(() => {
    const onFocus = (e) => {
      const { lat, lng, zoom = 17 } = e.detail || {};
      if (typeof lat !== "number" || typeof lng !== "number") return;
      map.flyTo([lat, lng], zoom, { duration: 1.2 });
    };
    window.addEventListener("istres-map:focus", onFocus);
    return () => window.removeEventListener("istres-map:focus", onFocus);
  }, [map]);
  return null;
}
